import ErrorCategory from './errorCategory';
import ErrorCodeDefinition from './errorCodeDefinition';
import LogLevel from './loglevel';

/**
 * if you are going to modify this file, please make sure to update the confluence page
 * https://theverygroup.atlassian.net/wiki/spaces/REB/pages/5473337400/Product+FE+logging+System
 */

const ErrorCodes: Record<string, ErrorCodeDefinition> = {
  // API Errors (00001-00999)
  API_REQUEST_FAILED: {
    code: 'PRDFE-00001',
    message: 'API request failed',
    level: LogLevel.ERROR,
    category: ErrorCategory.API,
    description: 'A request to an API endpoint failed',
  },
  API_TIMEOUT: {
    code: 'PRDFE-00002',
    message: 'API request timed out',
    level: LogLevel.ERROR,
    category: ErrorCategory.API,
    description: 'An API request exceeded the configured timeout',
  },
  API_INVALID_RESPONSE: {
    code: 'PRDFE-00003',
    message: 'Invalid API response',
    level: LogLevel.ERROR,
    category: ErrorCategory.API,
    description: 'The API returned a response that does not match the expected format',
  },
  CREDIT_OFFERS_API_REQUEST_ERROR: {
    code: 'PRDFE-00004',
    message: 'Credit Offers API request error',
    level: LogLevel.ERROR,
    category: ErrorCategory.API,
    description: 'An error occurred while making a request to the Credit Offers API',
  },

  // Product Errors (01000-01999)
  PRODUCT_NOT_FOUND: {
    code: 'PRDFE-01000',
    message: 'Product not found',
    level: LogLevel.ERROR,
    category: ErrorCategory.PRODUCT,
    description: 'The requested product could not be found',
  },
  PRODUCT_DATA_INVALID: {
    code: 'PRDFE-01001',
    message: 'Invalid product data',
    level: LogLevel.ERROR,
    category: ErrorCategory.PRODUCT,
    description: 'The product data is invalid or corrupt',
  },
  PRODUCT_OPTIONS_INVALID: {
    code: 'PRDFE-01002',
    message: 'Invalid product options',
    level: LogLevel.WARN,
    category: ErrorCategory.PRODUCT,
    description: 'The selected product options are invalid or unavailable',
  },

  // Cache Errors (02000-02999)
  REDIS_CONNECTION_ERROR: {
    code: 'PRDFE-02000',
    message: 'Redis connection error',
    level: LogLevel.ERROR,
    category: ErrorCategory.CACHE,
    description: 'Failed to establish connection with Redis',
  },
  REDIS_RECONNECT_ATTEMPT: {
    code: 'PRDFE-02003',
    message: 'Redis reconnect attempt',
    level: LogLevel.ERROR,
    category: ErrorCategory.CACHE,
    description: 'Temporary reconnect validation',
  },
  REDIS_RECONNECT_SUCCESS: {
    code: 'PRDFE-02004',
    message: 'Redis reconnected successfully',
    level: LogLevel.WARN,
    category: ErrorCategory.CACHE,
    description: 'Redis connection re-established after retry attempts',
  },
  REDIS_MOCK_DISCONNECT: {
    code: 'PRDFE-02005',
    message: 'Redis mock disconnect',
    level: LogLevel.ERROR,
    category: ErrorCategory.CACHE,
    description: 'Temporary reconnect validation',
  },
  REDIS_SET_CACHE_ERROR: {
    code: 'PRDFE-02001',
    message: 'Redis set cache error',
    level: LogLevel.ERROR,
    category: ErrorCategory.CACHE,
    description: 'Failed to set data in Redis cache',
  },
  REDIS_GET_CACHE_ERROR: {
    code: 'PRDFE-02002',
    message: 'Redis get cache error',
    level: LogLevel.ERROR,
    category: ErrorCategory.CACHE,
    description: 'Failed to retrieve data from Redis cache',
  },

  // UI Errors (03000-03999)
  UI_RENDER_ERROR: {
    code: 'PRDFE-03000',
    message: 'UI rendering error',
    level: LogLevel.ERROR,
    category: ErrorCategory.UI,
    description: 'A component failed to render correctly',
  },
  UI_EVENT_HANDLER_ERROR: {
    code: 'PRDFE-03001',
    message: 'UI event handler error',
    level: LogLevel.ERROR,
    category: ErrorCategory.UI,
    description: 'An error occurred in a UI event handler',
  },

  // Authentication Errors (04000-04999)
  AUTH_FAILED: {
    code: 'PRDFE-04000',
    message: 'Authentication failed',
    level: LogLevel.ERROR,
    category: ErrorCategory.AUTH,
    description: 'User authentication attempt failed',
  },
  SESSION_EXPIRED: {
    code: 'PRDFE-04001',
    message: 'User session expired',
    level: LogLevel.WARN,
    category: ErrorCategory.AUTH,
    description: 'User session has expired',
  },

  // Basket Errors (05000-05999)
  BASKET_ADD_ITEM_FAILED: {
    code: 'PRDFE-05000',
    message: 'Failed to add item to basket',
    level: LogLevel.ERROR,
    category: ErrorCategory.BASKET,
    description: 'An attempt to add an item to the basket failed',
  },
  BASKET_AMEND_ITEM_FAILED: {
    code: 'PRDFE-05001',
    message: 'Failed to amend item in basket',
    level: LogLevel.ERROR,
    category: ErrorCategory.BASKET,
    description: 'An attempt to amend the basket failed',
  },
  BASKET_SERVICE_ERROR: {
    code: 'PRDFE-05002',
    message: 'Basket service error',
    level: LogLevel.ERROR,
    category: ErrorCategory.BASKET,
    description: 'An error occurred in the basket service',
  },

  // save for later
  SAVE_FOR_LATER_ADD_ITEM_FAILED: {
    code: 'PRDFE-06001',
    message: 'Failed to add item to save for later',
    level: LogLevel.ERROR,
    category: ErrorCategory.SAVEFORLATER,
    description: 'An attempt to add an item to save for later failed',
  },

  SAVE_FOR_LATER_REMOVE_ITEM_FAILED: {
    code: 'PRDFE-06002',
    message: 'Failed to remove item from save for later',
    level: LogLevel.ERROR,
    category: ErrorCategory.SAVEFORLATER,
    description: 'An attempt to remove an item from save for later failed',
  },

  SAVE_FOR_LATER_EXISTING_ITEM: {
    code: 'PRDFE-06003',
    message: 'Failed to get existing item from save for later',
    level: LogLevel.ERROR,
    category: ErrorCategory.SAVEFORLATER,
    description: 'An attempt to get existing item from save for later failed',
  },

  SAVE_FOR_LATER_SERVICE_ERROR: {
    code: 'PRDFE-06004',
    message: 'Save for later service error',
    level: LogLevel.ERROR,
    category: ErrorCategory.SAVEFORLATER,
    description: 'An error occurred in the save for later service',
  },

  // Customer Errors (07000-07999)
  CUSTOMER_GET_CUSTOMER_FAILED: {
    code: 'PRDFE-07000',
    message: 'Failed to get customer',
    level: LogLevel.ERROR,
    category: ErrorCategory.CUSTOMER,
    description: 'An attempt to get customer failed',
  },

  // System Errors (09000-09999)
  SYSTEM_RESOURCE_UNAVAILABLE: {
    code: 'PRDFE-09000',
    message: 'System resource unavailable',
    level: LogLevel.ERROR,
    category: ErrorCategory.SYSTEM,
    description: 'A required system resource is unavailable',
  },
  SYSTEM_CONFIG_ERROR: {
    code: 'PRDFE-09001',
    message: 'System configuration error',
    level: LogLevel.ERROR,
    category: ErrorCategory.SYSTEM,
    description: 'System is improperly configured',
  },
  SYSTEM_FAILED_TO_START: {
    code: 'PRDFE-09002',
    message: 'System failed to start',
    level: LogLevel.ERROR,
    category: ErrorCategory.SYSTEM,
    description: 'System failed to start',
  },
  SYSTEM_ERROR: {
    code: 'PRDFE-09003',
    message: 'System error',
    level: LogLevel.ERROR,
    category: ErrorCategory.SYSTEM,
    description: 'System error',
  },

  CSRF_TOKEN_ERROR: {
    code: 'PRDFE-09003',
    message: 'CSRF token error',
    level: LogLevel.ERROR,
    category: ErrorCategory.CLIENT,
    description: 'CSRF token validation failed',
  },
  SSME_ERROR: {
    code: 'PRDFE-09004',
    message: 'SSME error',
    level: LogLevel.ERROR,
    category: ErrorCategory.SYSTEM,
    description: 'SSME service error',
  },
  SSME_TIMEOUT: {
    code: 'PRDFE-09005',
    message: 'SSME timeout',
    level: LogLevel.ERROR,
    category: ErrorCategory.SYSTEM,
    description: 'SSME service timeout',
  },
};

export default ErrorCodes;
